import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { deckService } from "@/services/firestore";
import { Deck } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2, Trash2, AlertTriangle, Settings, Globe, Lock, Save, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Helmet } from "react-helmet-async";

export default function EditDeckPage() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuthStore();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [frontLang, setFrontLang] = useState("en-US");
  const [backLang, setBackLang] = useState("vi-VN");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && id) {
      const fetchDeck = async () => {
        try {
          const data = await deckService.getDeck(id);
          if (data) {
            setDeck(data);
            setTitle(data.title);
            setDescription(data.description || "");
            setIsPublic(data.isPublic);
            setFrontLang(data.frontLang || "en-US");
            setBackLang(data.backLang || "vi-VN");
          } else {
            navigate("/decks");
          }
        } catch (error) {
          console.error("Error fetching deck:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchDeck();
    }
  }, [user, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    
    setSubmitting(true);
    try {
      await deckService.updateDeck(id, {
        title,
        description,
        isPublic,
        frontLang,
        backLang
      });
      navigate(`/decks/${id}`);
    } catch (error) {
      console.error("Error updating deck:", error);
      alert("Failed to update deck");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deckService.deleteDeck(id);
      navigate("/decks");
    } catch (error) {
      console.error("Error deleting deck:", error);
      alert("Failed to delete deck");
      setDeleting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 md:py-16 px-4 md:px-6">
      <Helmet>
        <title>Chỉnh sửa: {deck?.title} | Flashcard Platform</title>
      </Helmet>

      <Link 
        to={`/decks/${id}`} 
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "mb-8 rounded-xl font-bold hover:bg-muted/50")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Quay lại bộ thẻ
      </Link>

      <div className="grid grid-cols-1 gap-10">
        <Card className="border-2 rounded-[3rem] shadow-xl overflow-hidden backdrop-blur-sm bg-background/50">
          <CardHeader className="p-10 pb-6 border-b-2 border-dashed border-muted-foreground/10 bg-muted/20">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                <Settings className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl font-black tracking-tight">Cài đặt bộ thẻ</CardTitle>
                <CardDescription className="text-base font-medium">
                  Cập nhật thông tin và quyền riêng tư cho bộ thẻ của bạn.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-10 space-y-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-3">
                <Label htmlFor="title" className="font-bold text-sm uppercase tracking-widest text-muted-foreground ml-1">Tiêu đề bộ thẻ</Label>
                <Input 
                  id="title" 
                  value={title}
                  placeholder="Ví dụ: 1000 từ vựng Oxford..."
                  className="h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg font-bold p-4 transition-all"
                  onChange={(e) => setTitle(e.target.value)}
                  required 
                />
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="description" className="font-bold text-sm uppercase tracking-widest text-muted-foreground ml-1">Mô tả (Tùy chọn)</Label>
                <Textarea 
                  id="description" 
                  placeholder="Bộ thẻ này dùng để ôn tập..."
                  className="min-h-[120px] rounded-2xl border-2 focus-visible:ring-primary/20 text-lg p-4 transition-all"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="space-y-4">
                <Label className="font-bold text-sm uppercase tracking-widest text-muted-foreground ml-1">Ngôn ngữ mặc định (TTS)</Label>
                <div className="grid grid-cols-2 gap-4 p-6 rounded-[2rem] border-2 bg-muted/5 group transition-all">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground pl-1">Mặt trước</Label>
                    <select 
                      value={frontLang}
                      onChange={(e) => setFrontLang(e.target.value)}
                      className="w-full h-12 rounded-xl border-2 bg-white px-4 text-sm font-bold focus:border-primary transition-all outline-none"
                    >
                      <option value="en-US">English (US)</option>
                      <option value="vi-VN">Tiếng Việt</option>
                      <option value="ja-JP">Japanese</option>
                      <option value="ko-KR">Korean</option>
                      <option value="fr-FR">French</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground pl-1">Mặt sau</Label>
                    <select 
                      value={backLang}
                      onChange={(e) => setBackLang(e.target.value)}
                      className="w-full h-12 rounded-xl border-2 bg-white px-4 text-sm font-bold focus:border-primary transition-all outline-none"
                    >
                      <option value="vi-VN">Tiếng Việt</option>
                      <option value="en-US">English (US)</option>
                      <option value="ja-JP">Japanese</option>
                      <option value="ko-KR">Korean</option>
                      <option value="fr-FR">French</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Label className="font-bold text-sm uppercase tracking-widest text-muted-foreground ml-1">Chế độ hiển thị</Label>
                <div className="flex items-center justify-between p-6 rounded-[2rem] border-2 bg-muted/10 hover:bg-muted/20 transition-all group">
                  <div className="flex items-center gap-4">
                    <div className={cn("p-3 rounded-2xl transition-all", isPublic ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                      {isPublic ? <Globe className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
                    </div>
                    <div className="space-y-0.5">
                      <Label htmlFor="public" className="text-lg font-black block cursor-pointer">Công khai bộ thẻ</Label>
                      <p className="text-sm font-medium text-muted-foreground">
                        Cho phép người dùng khác tìm kiếm và sao chép bộ thẻ này.
                      </p>
                    </div>
                  </div>
                  <Switch 
                    id="public" 
                    checked={isPublic}
                    className="data-[state=checked]:bg-primary"
                    onCheckedChange={setIsPublic}
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" size="lg" className="flex-1 h-16 rounded-2xl text-xl font-black shadow-xl shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98]" disabled={submitting}>
                  {submitting ? <Loader2 className="mr-2 h-6 w-6 animate-spin" /> : <Save className="mr-2 h-6 w-6" />}
                  Lưu thay đổi
                </Button>
                <Link 
                  to={`/decks/${id}`} 
                  className={cn(buttonVariants({ variant: "outline", size: "lg" }), "flex-1 h-16 rounded-2xl text-xl font-bold border-2 hover:bg-muted/50")}
                >
                  <X className="mr-2 h-6 w-6" /> Hủy
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-2 border-destructive/20 bg-destructive/5 overflow-hidden rounded-[3rem]">
          <CardHeader className="p-10 pb-6 border-b-2 border-dashed border-destructive/10">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-destructive text-destructive-foreground shadow-lg shadow-destructive/20">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <CardTitle className="text-2xl font-black text-destructive tracking-tight">Vùng nguy hiểm</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-10">
            <p className="text-lg font-medium text-destructive/80 mb-8 leading-relaxed">
              Xóa bộ thẻ là hành động vĩnh viễn và sẽ loại bỏ toàn bộ các thẻ học bên trong. Hãy cẩn trọng trước khi tiếp tục.
            </p>
            <Dialog>
              <DialogTrigger render={<Button variant="destructive" size="lg" className="w-full h-16 rounded-2xl text-xl font-black shadow-xl shadow-destructive/20 transition-all hover:bg-destructive">
                  <Trash2 className="mr-2 h-6 w-6" /> Xóa bộ thẻ này
                </Button>} />
              <DialogContent className="rounded-[2.5rem] border-2">
                <DialogHeader className="p-4">
                  <DialogTitle className="text-3xl font-black text-destructive">Xác nhận xóa?</DialogTitle>
                  <DialogDescription className="text-lg font-medium py-4">
                    Hành động này không thể hoàn tác. Bạn sẽ mất vĩnh viễn bộ thẻ "<strong>{deck?.title}</strong>" và toàn bộ dữ liệu học tập liên quan.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-3 p-4">
                  <Button variant="outline" className="h-14 rounded-2xl font-bold flex-1 text-lg border-2" type="button">Quay lại</Button>
                  <Button variant="destructive" className="h-14 rounded-2xl font-black flex-1 text-lg" onClick={handleDelete} disabled={deleting}>
                    {deleting ? "Đang xóa..." : "Xóa vĩnh viễn"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
